import React, { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useJustOneRoom } from "./useJustOneRoom"
import { getIdentity } from "./justOneRoom"
import { Screen, JoButton, PlayerDot, C, F, useJustOneFonts } from "./JustOneUI"
import LobbyScreen from "./LobbyScreen"
import ClueScreen from "./ClueScreen"
import ReviewScreen from "./ReviewScreen"
import GuessScreen from "./GuessScreen"
import RevealScreen from "./RevealScreen"
import GameOverScreen from "./GameOverScreen"
import { playSound } from "../../../utils/ballparkSounds"

const JustOneGame = () => {
  useJustOneFonts()
  const { code } = useParams()
  const navigate = useNavigate()
  const { room, loading, error } = useJustOneRoom(code)
  const { playerId } = getIdentity()
  const [showScores, setShowScores] = useState(false)

  // Follow the host to a rematch room
  useEffect(() => {
    if (room?.nextRoom && room.phase === "gameover") {
      navigate(`/justone/${room.nextRoom}`)
    }
  }, [room?.nextRoom, room?.phase, navigate])

  if (loading) {
    return (
      <Screen style={{ justifyContent: "center", alignItems: "center" }}>
        <div style={{ fontWeight: 800, color: C.muted, fontFamily: F.display }}>Loading…</div>
      </Screen>
    )
  }

  if (error || !room) {
    return (
      <Screen style={{ justifyContent: "center", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 24, color: C.cyan }}>
          Game Not Found
        </div>
        <div style={{ color: C.muted, fontWeight: 700, textAlign: "center" }}>
          That room may have expired. Codes last about 12 hours.
        </div>
        <Link to="/justone" style={{ width: "100%", textDecoration: "none" }}>
          <JoButton>Back to Just One</JoButton>
        </Link>
      </Screen>
    )
  }

  // A visitor with a link who never joined: bounce to landing to enter a name
  if (!room.players?.[playerId]) {
    return (
      <Screen style={{ justifyContent: "center", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 24, color: C.cyan }}>
          You're not in this game yet
        </div>
        <Link to="/justone" style={{ width: "100%", textDecoration: "none" }}>
          <JoButton>Join with Room Code {code}</JoButton>
        </Link>
      </Screen>
    )
  }

  const isHost = room.hostId === playerId
  const props = { room, code, playerId, isHost, setShowScores }

  return (
    <div style={{ position: "relative", overflow: "hidden", width: "100%" }}>
      {/* Standings Drawer */}
      <div
        style={{
          position: "absolute",
          top: showScores ? 0 : "-100%",
          left: 0,
          right: 0,
          background: C.card,
          borderBottom: `3.5px solid ${C.cyan}`,
          boxShadow: "0 10px 30px rgba(26, 37, 60, 0.15)",
          zIndex: 100,
          transition: "top 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          padding: "24px 20px 20px",
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
          maxWidth: 520,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 20, color: C.ink }}>
            🏆 Current Standings
          </div>
          <button
            onClick={() => {
              playSound("click")
              setShowScores(false)
            }}
            style={{
              background: "none",
              border: "none",
              fontFamily: F.display,
              fontWeight: 800,
              fontSize: 26,
              color: C.coral,
              cursor: "pointer",
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Object.entries(room.players)
            .sort((a, b) => b[1].score - a[1].score)
            .map(([pid, p], i) => (
              <div
                key={pid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: C.bg,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 14,
                  padding: "10px 12px",
                }}
              >
                <span style={{ fontWeight: 800, fontSize: 14, color: C.muted, width: 16 }}>
                  {i + 1}
                </span>
                <PlayerDot color={p.color} size={24} />
                <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>
                  {p.name} {pid === playerId && " (You)"}
                </span>
                <span style={{ marginLeft: "auto", fontFamily: F.display, fontWeight: 900, fontSize: 18, color: C.cyan }}>
                  {p.score} pts
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Floating Standings Button in active round phases */}
      {room.phase !== "lobby" && room.phase !== "gameover" && (
        <button
          onClick={() => {
            playSound("click")
            setShowScores(true)
          }}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            background: C.card,
            border: `1.5px solid ${C.border}`,
            borderRadius: 12,
            padding: "6px 12px",
            fontFamily: F.display,
            fontWeight: 800,
            fontSize: 12,
            color: C.cyan,
            cursor: "pointer",
            zIndex: 10,
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          🏆 Standings
        </button>
      )}

      {/* Screen Render */}
      {(() => {
        switch (room.phase) {
          case "lobby":
            return <LobbyScreen {...props} />
          case "clue":
            return <ClueScreen {...props} />
          case "review":
            return <ReviewScreen {...props} />
          case "guess":
            return <GuessScreen {...props} />
          case "reveal":
            return <RevealScreen {...props} />
          case "gameover":
            return <GameOverScreen {...props} />
          default:
            return null
        }
      })()}
    </div>
  )
}

export default JustOneGame
